// Helpers for turning a long ship description into something easy to scan.

const norm = (s) => String(s || "").trim().toLowerCase();

// Plain-text version of a markdown description (for short previews).
export function plainText(md) {
  return String(md || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/[*_`>~]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function preview(md, max = 200) {
  const t = plainText(md);
  if (t.length <= max) return { text: t, truncated: false };
  const cut = t.slice(0, max);
  return { text: cut.slice(0, cut.lastIndexOf(" ") > 120 ? cut.lastIndexOf(" ") : max).trim() + "…", truncated: true };
}

// A bare line like "Overview" or "History" (pasted from a wiki page, often with no blank
// lines around it) is treated as a heading: short, no sentence punctuation, and followed by content.
const looksLikeBareHeading = (line, lines, i) => {
  if (!line || line.length > 32) return false;
  if (!/^[A-Za-z][A-Za-z0-9 &'’/-]*$/.test(line)) return false;
  if (/[.!?,;:]$/.test(line) || line.split(/\s+/).length > 4) return false;
  const next = lines.slice(i + 1).find((l) => l.trim() !== "");
  return next !== undefined;
};

/**
 * Splits a description into [{ id, title, body }]. Understands markdown headings
 * (# / ## / ###) and bare heading lines. Text before the first heading becomes "Summary".
 * `skipTitle` drops a leading heading that just repeats the ship's name.
 */
export function splitSections(description, skipTitle = "") {
  const lines = String(description || "").replace(/\r\n?/g, "\n").split("\n");
  const sections = [];
  let current = { title: "Summary", body: [] };
  const push = () => {
    const body = current.body.join("\n").trim();
    if (body) sections.push({ title: current.title, body });
  };

  lines.forEach((raw, i) => {
    const md = raw.match(/^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/);
    let title = null;
    if (md) title = md[2].replace(/[*_`]/g, "").trim();
    else if (looksLikeBareHeading(raw.trim(), lines, i)) title = raw.trim();

    if (title) {
      if (norm(title) === norm(skipTitle) && sections.length === 0 && current.body.join("").trim() === "") return;
      push();
      current = { title, body: [] };
    } else {
      current.body.push(raw);
    }
  });
  push();

  // give each section a stable, unique id
  const seen = {};
  return sections.map((s) => {
    const base = s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "section";
    seen[base] = (seen[base] || 0) + 1;
    return { ...s, id: seen[base] > 1 ? `${base}-${seen[base]}` : base };
  });
}

// The structured facts we show as a quick-facts grid.
export const FACT_FIELDS = [
  ["type", "Type"], ["date", "Date"], ["builder", "Builder"], ["owner", "Owner"],
  ["port_of_registry", "Port of registry"], ["imo", "IMO"], ["tonnage", "Tonnage"],
  ["length", "Length"], ["beam", "Beam"], ["draught", "Draught"], ["capacity", "Capacity"], ["crew", "Crew"],
];

export const knownFacts = (ship) =>
  FACT_FIELDS
    .map(([k, label]) => [label, ship[k]])
    .filter(([, v]) => v !== undefined && v !== null && v !== "" && v !== "unknown" && v !== 0 && v !== "0")
    .map(([label, v]) => [label, String(v)]);
